<script setup lang="ts">
import { RouterView, useRouter, useRoute } from "vue-router";
import { IS_NATIVE } from "@/config/apiBase";
import { trackLogin } from "@/utils/trackPageVisit";
import { Toaster, toast } from 'vue-sonner'
import { onMounted, ref, onBeforeUnmount } from 'vue'
import axios from 'axios'
// @ts-ignore
import { useDynamicMetadata } from '@/composables/useDynamicMetadata'
import AuthModal from './components/pages/Models/AuthModal.vue'
import FeedbackBubble from './components/pages/FeedbackBubble.vue'
// @ts-ignore
import { connectSocket } from './utils/socketClient'
// @ts-ignore
import { startMessageNotifications, primeNotificationSound } from './utils/messageNotifier'
// @ts-ignore
import { resetSessionExpiry, handleSessionEndedElsewhere } from './utils/sessionExpiry'
// @ts-ignore
import { startSessionRefresh } from './utils/sessionRefresh'

// Initialize dynamic metadata for all pages
useDynamicMetadata()

import { useProductionMetaTags } from './metaTags.prod'
useProductionMetaTags()

// --- Mobile detection for bottom bar ---
const isMobile = ref(window.innerWidth <= 600)

function handleResize() {
  isMobile.value = window.innerWidth <= 600
}

onMounted(() => {
  window.addEventListener('resize', handleResize)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})

// ── Native app (Capacitor) chrome ───────────────────────────────────────────
// Android hardware back navigates the SPA history; at the history root it
// backgrounds the app. Status bar gets an explicit style so text is readable
// over the light header. Guarded by IS_NATIVE — dead code on web.
if (IS_NATIVE) {
  onMounted(async () => {
    try {
      const { App: CapApp } = await import('@capacitor/app')
      CapApp.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) window.history.back()
        else CapApp.minimizeApp()
      })
      const { StatusBar, Style } = await import('@capacitor/status-bar')
      await StatusBar.setStyle({ style: Style.Light })
    } catch (e) {
      console.warn('[native] chrome setup failed:', e?.message)
    }
  })
}

// Modal state for login
const showModal = ref(false)
const modalMode = ref('login')

function openModal(type: string) {
  modalMode.value = type
  showModal.value = true
}

function isLoggedIn() {
  return !!localStorage.getItem('auth-token')
}

// The router guard fires this when a logged-out user opens a login-only page.
const openLoginFromRouter = () => openModal('login')
onMounted(() => window.addEventListener('renants:open-login', openLoginFromRouter))
onBeforeUnmount(() => window.removeEventListener('renants:open-login', openLoginFromRouter))

// Feedback bubble ref
const feedbackBubble = ref<any>(null)
function openFeedback() {
  if (feedbackBubble.value) {
    feedbackBubble.value.visible = true
    feedbackBubble.value.expanded = true
  }
}

// Helper to decode JWT payload (without verification - server already verified)
function decodeJwtPayload(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

// Recover auth token from cookies if localStorage is empty (e.g., after Stripe redirect)
function recoverAuthTokenFromCookie() {
  const existingToken = localStorage.getItem('auth-token');
  if (existingToken) return; // Already have token
  
  // Check if there's a jwt-token cookie (preferred) or auth-token cookie
  const cookies = document.cookie.split(';').map(c => c.trim());
  
  // First try jwt-token (the actual JWT)
  for (const cookie of cookies) {
    if (cookie.startsWith('jwt-token=')) {
      const tokenValue = cookie.substring('jwt-token='.length);
      if (tokenValue) {
        console.log('[App] Recovered jwt-token from cookie');
        localStorage.setItem('auth-token', tokenValue);
        return;
      }
    }
  }
  
  // Fallback to auth-token cookie (JSON-encoded, less ideal but workable)
  for (const cookie of cookies) {
    if (cookie.startsWith('auth-token=')) {
      try {
        const decodedValue = decodeURIComponent(cookie.substring('auth-token='.length));
        const authObj = JSON.parse(decodedValue);
        // We have the auth object but not the JWT - this is not ideal
        // The server should have set jwt-token cookie instead
        console.log('[App] Found auth-token cookie but no jwt-token - recovery incomplete');
        return;
      } catch (e) {
        console.log('[App] Failed to parse auth-token cookie:', e.message);
      }
    }
  }
}

// Apply a JWT delivered by the redirect-based OAuth flow. Reached two ways:
// web — the callback redirects to /?auth_token=... and this runs on the fresh
// page load; native — the callback redirects to the com.renants.app:// deep
// link and the appUrlOpen listener below hands the token in.
const oauthRouter = useRouter()
async function applyOAuthToken(authToken: string) {
  // Store the token
  localStorage.setItem('auth-token', authToken);

  // Decode JWT to get user info directly
  const payload = decodeJwtPayload(authToken);
  if (payload && payload.userID) {
    localStorage.setItem('userid', payload.userID);
    localStorage.setItem('username', payload.userName || '');
    localStorage.setItem('email', payload.email || '');
    toast.success('Login successful!');
  } else {
    // Fallback: Fetch user info from server using email from token
    try {
      const userRes = await axios.post('/api/getoneusers', {
        email: payload?.email
      });
      if (userRes.data && (userRes.data.item || userRes.data[0])) {
        const user = userRes.data.item || userRes.data[0];
        localStorage.setItem('userid', user.userID);
        localStorage.setItem('username', user.userName);
        localStorage.setItem('email', user.email);
      }
      toast.success('Login successful!');
    } catch (e) {
      console.error('Failed to fetch user info after OAuth:', e);
    }
  }

  // Record the login in the admin "Authenticated Users" report.
  await trackLogin('Google', {
    userID: localStorage.getItem('userid') || '',
    userName: localStorage.getItem('username') || '',
  });

  // Check if there's a redirect target stored
  const redirectTo = localStorage.getItem('redirectTo');
  if (redirectTo) {
    localStorage.removeItem('redirectTo');
    oauthRouter.push(redirectTo);
  }
}

// Handle OAuth token/error from URL (redirect-based OAuth flow)
onMounted(async () => {
  // First, try to recover token from cookies if localStorage is empty
  recoverAuthTokenFromCookie();

  const urlParams = new URLSearchParams(window.location.search);
  const authToken = urlParams.get('auth_token');
  const authError = urlParams.get('auth_error');

  // Handle OAuth error
  if (authError) {
    toast.error(authError || 'Login failed');
    // Clean URL (same doubled-slash collapse as the token branch)
    const cleanUrl = window.location.pathname.replace(/\/{2,}/g, '/') || '/';
    window.history.replaceState({}, document.title, cleanUrl);
    return;
  }

  if (authToken) {
    await applyOAuthToken(authToken);
    // Clean URL (remove auth_token param). Collapse doubled slashes first:
    // a pathname of "//" is a protocol-relative reference, so replaceState
    // resolves it to an empty-host URL and throws a SecurityError.
    const cleanUrl = window.location.pathname.replace(/\/{2,}/g, '/') || '/';
    window.history.replaceState({}, document.title, cleanUrl);
  }
});

// Native: the OAuth callback redirects to com.renants.app://auth instead of a
// page load, so the token arrives as a deep link while the app is running.
if (IS_NATIVE) {
  onMounted(async () => {
    try {
      const { App: CapApp } = await import('@capacitor/app')
      CapApp.addListener('appUrlOpen', async ({ url }) => {
        let parsed: URL
        try { parsed = new URL(url) } catch { return }
        if (parsed.protocol !== 'com.renants.app:') return
        // Dismiss the in-app browser the Google flow ran in.
        try {
          const { Browser } = await import('@capacitor/browser')
          await Browser.close()
        } catch { /* already closed */ }
        const token = parsed.searchParams.get('auth_token')
        const error = parsed.searchParams.get('auth_error')
        if (error) {
          toast.error(error || 'Login failed')
        } else if (token) {
          await applyOAuthToken(token)
          // Already-mounted components read auth state from localStorage on
          // create — reload the WebView so they pick up the signed-in session,
          // mirroring the full page load the web flow gets from its redirect.
          window.location.reload()
        }
      })
    } catch (e: any) {
      console.warn('[native] deep-link listener failed:', e?.message)
    }
  })
}

// Presence: hold the Socket.IO connection open for the whole signed-in session,
// on every page. It used to belong to MessageSection.vue, so a user showed as
// online only while their Messages tab was mounted.
//
// Registered after the block above so a token recovered from a cookie or an
// OAuth redirect is already in localStorage. connectSocket() is idempotent, so
// running it again on each navigation is free once connected — it exists to
// catch a login that happened on some other page without a reload.
const socketRouter = useRouter()

// The new-message sound and title flash hang off that same connection, so they
// work on every page instead of only while the Messages tab is mounted.
function openRealtimeChannel() {
  connectSocket()
  startMessageNotifications()
}

onMounted(() => {
  // Browsers block programmatic playback until the user has interacted with the
  // page, so arm the unlock now and spend the first click on it.
  primeNotificationSound()
  openRealtimeChannel()
})

socketRouter.afterEach(() => {
  openRealtimeChannel()
})

// Email sign-in used to finish with window.location.reload() so every
// component re-read the session from localStorage — which re-downloaded and
// re-parsed the whole app and re-ran every API call. Remounting the routed
// page does the same job in place: the navbar (rendered inside each page)
// re-verifies the token and the page refetches as the signed-in user.
const viewKey = ref(0)
function onLoggedIn() {
  resetSessionExpiry()
  openRealtimeChannel()
  viewKey.value++
}
onMounted(() => window.addEventListener('renants:logged-in', onLoggedIn))
onBeforeUnmount(() => window.removeEventListener('renants:logged-in', onLoggedIn))

// Keep open tabs in step. localStorage is shared, but each tab only re-reads
// it on mount — so signing in again in one tab left every other tab showing
// "session expired" and a signed-out navbar, and signing out in one tab made
// the others raise a bogus "session expired" on their next request.
// A token *replaced* by another value is just a sliding-session refresh, which
// every request already picks up from localStorage, so that is ignored.
function onStorage(e: StorageEvent) {
  if (e.key !== 'auth-token' && e.key !== null) return
  const hasToken = !!localStorage.getItem('auth-token')
  if (hasToken && e.key === 'auth-token' && !e.oldValue) {
    // Signed in elsewhere. The login writes userid/username right after the
    // token, so give it a moment before re-reading everything.
    showModal.value = false
    setTimeout(onLoggedIn, 100)
  } else if (!hasToken && (e.key === null || e.oldValue)) {
    handleSessionEndedElsewhere()
    viewKey.value++
  }
}
onMounted(() => {
  window.addEventListener('storage', onStorage)
  startSessionRefresh()
})
onBeforeUnmount(() => window.removeEventListener('storage', onStorage))
</script>

<template>
  <main>
    <RouterView :key="viewKey" />
  </main>
  <Toaster position="top-right" richColors closeButton />
  <FeedbackBubble ref="feedbackBubble" />
  <AuthModal
    v-if="showModal"
    :mode="modalMode"
    @close="showModal = false"
    @switchMode="modalMode = $event"
  />
  <!-- Mobile Bottom Bar (global, all pages) -->
  <nav v-if="isMobile" class="mobile-bottom-bar">
    <button class="bar-btn" @click="$router.push('/')">
      <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24">
        <path d="M3 9.5L12 4l9 5.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9.5z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
      <span>Home</span>
    </button>
    <button class="bar-btn" @click="
      isLoggedIn() ? $router.push('/favorites') : openModal('login')
    ">
      <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41 0.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
      <span>Favorites</span>
    </button>
    <!-- <button class="bar-btn" @click="$router.push('/my-adds')">
      <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M16 3v4a1 1 0 0 0 1 1h4"/></svg>
      <span>My Adds</span>
    </button> -->
    <button class="bar-btn" @click="
      isLoggedIn() ? $router.push('/messages') : openModal('login')
    ">
      <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
      <span>Messages</span>
    </button>
    <button class="bar-btn"  @click="
      isLoggedIn() ? $router.push({ path: '/pricing', query: { src: 'mobile-nav' } }) : openModal('login')
    ">
      <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M12 2l2 7h7l-5.5 4 2 7-5.5-4-5.5 4 2-7L3 9h7z"/></svg>
      <span>Upgrade</span>
    </button>
    <button class="bar-btn" @click="openFeedback">
      <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><line x1="9" y1="10" x2="15" y2="10"/><line x1="12" y1="7" x2="12" y2="13"/></svg>
      <span>Feedback</span>
    </button>
  </nav>
</template>


<style>
/* Global body reset with safe area support */
html, body {
  margin: 0;
  padding: 0;
  min-height: 100vh;
  overflow-x: hidden;
  max-width: 100vw;
}
body {
  padding-left: env(safe-area-inset-left, 0);
  padding-right: env(safe-area-inset-right, 0);
  padding-bottom: env(safe-area-inset-bottom, 0);
}

/* Main content wrapper - ensures content respects device safe areas */
main {
  min-height: 100vh;
  padding-bottom: calc(56px + env(safe-area-inset-bottom, 0px));
}


.mobile-bottom-bar {
  display: none;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(48px + env(safe-area-inset-bottom, 0px));
  padding-bottom: env(safe-area-inset-bottom, 0px);
  background: #fff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -2px 8px rgba(52,103,213,0.07);
  z-index: 1000;
  width: 100%;
  padding-left: 4px;
  padding-right: 4px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-radius: 12px 12px 0 0;
  font-family: "Inter", sans-serif;
  transition: height 0.18s cubic-bezier(.4,0,.2,1), box-shadow 0.18s;
}
.bar-btn {
  flex: 1;
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #3467d5;
  padding: 6px 0 0;
  height: 48px;
  cursor: pointer;
  transition: color 0.18s, background 0.18s, box-shadow 0.18s;
  border-radius: 10px;
  margin: 0 2px;
  position: relative;
}
.bar-btn svg {
  margin-bottom: 1px;
  color: #3467d5;
  width: 22px;
  height: 22px;
  filter: drop-shadow(0 1px 3px rgba(52,103,213,0.08));
  transition: width 0.18s, height 0.18s;
}
.bar-btn span {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: #3467d5;
  margin-top: 0;
}
.bar-btn:active, .bar-btn:focus {
  color: #274b9a;
  background: #e0e7ff;
  box-shadow: 0 1px 4px rgba(52,103,213,0.10);
}
.bar-btn:hover {
  background: #f0f4ff;
  color: #274b9a;
}
.bar-btn.active {
  background: linear-gradient(135deg, #3467d5 0%, #5b87f0 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(52,103,213,0.14);
}
.bar-btn.active span,
.bar-btn.active svg {
  color: #fff !important;
}
@media (max-width: 500px) {
  .mobile-bottom-bar {
    display: flex;
  }
}

</style>
